'use client';

import { useEffect } from 'react';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import * as CookieConsentLibrary from 'vanilla-cookieconsent';
import type { CookieConsentConfig } from 'vanilla-cookieconsent';

import { segment } from '@/lib/tracking/segmentAnalytics';
import { initialiseSegmentAnalytics } from '@/lib/tracking/segmentAnalytics';

const CAT_NECESSARY = 'necessary';
const CAT_ANALYTICS = 'analytics';
const CAT_ADVERTISEMENT = 'advertisement';

const SERVICE_AD_STORAGE = 'ad_storage';
const SERVICE_AD_USER_DATA = 'ad_user_data';
const SERVICE_AD_PERSONALIZATION = 'ad_personalization';
const SERVICE_ANALYTICS_STORAGE = 'analytics_storage';

const initaliseAnalytics = () => {
    // moving this out to always have segment loaded
    initialiseSegmentAnalytics();
};

const updateGtagConsent = () => {
    segment.ready(() => {
        if (!window.gtag) return;

        window.gtag('consent', 'update', {
            [SERVICE_AD_STORAGE]: CookieConsentLibrary.acceptedService(
                SERVICE_AD_STORAGE,
                CAT_ADVERTISEMENT,
            )
                ? 'granted'
                : 'denied',
            [SERVICE_AD_USER_DATA]: CookieConsentLibrary.acceptedService(
                SERVICE_AD_USER_DATA,
                CAT_ADVERTISEMENT,
            )
                ? 'granted'
                : 'denied',
            [SERVICE_AD_PERSONALIZATION]: CookieConsentLibrary.acceptedService(
                SERVICE_AD_PERSONALIZATION,
                CAT_ADVERTISEMENT,
            )
                ? 'granted'
                : 'denied',
            [SERVICE_ANALYTICS_STORAGE]: CookieConsentLibrary.acceptedService(
                SERVICE_ANALYTICS_STORAGE,
                CAT_ANALYTICS,
            )
                ? 'granted'
                : 'denied',
        });
    });
};

/**
 * All config. options available here:
 * https://cookieconsent.orestbida.com/reference/configuration-reference.html
 */
const config: CookieConsentConfig = {
    guiOptions: {
        consentModal: {
            layout: 'box',
            position: 'bottom left',
            flipButtons: false,
            equalWeightButtons: false,
        },
        preferencesModal: {
            layout: 'box',
            flipButtons: false,
            equalWeightButtons: false,
        },
    },
    categories: {
        [CAT_NECESSARY]: {
            enabled: true, // this category is enabled by default
            readOnly: true, // this category cannot be disabled
        },
        [CAT_ANALYTICS]: {
            autoClear: {
                cookies: [
                    {
                        name: /^ajs/, // regex: match all cookies starting with '_ga'
                    },
                    {
                        name: /^ph/, // regex: match all cookies starting with '_ga'
                    },
                    {
                        name: '_gid', // string: exact cookie name
                    },
                ],
            },
            // See: https://cookieconsent.orestbida.com/reference/configuration-reference.html#category-services
            services: {
                [SERVICE_ANALYTICS_STORAGE]: {
                    label: 'Enables storage (such as cookies) related to analytics e.g. visit duration.',
                },
            },
        },
        [CAT_ADVERTISEMENT]: {
            services: {
                [SERVICE_AD_STORAGE]: {
                    label: 'Enables storage (such as cookies) related to advertising.',
                },
                [SERVICE_AD_USER_DATA]: {
                    label: 'Sets consent for sending user data related to advertising to Google.',
                },
                [SERVICE_AD_PERSONALIZATION]: {
                    label: 'Sets consent for personalized advertising.',
                },
            },
        },
    },
    language: {
        default: 'en',
        translations: {
            en: {
                consentModal: {
                    title: 'Cookie consent',
                    description:
                        'This website uses cookies to enhance your browsing experience and analyze web traffic. <br /><br />' +
                        'Please accept or manage your cookie settings below. Change your preferences at any time by ' +
                        'clicking <italic>Manage Cookies</italic> in the bottom of every page of this website.',
                    acceptAllBtn: 'Accept cookies',
                    acceptNecessaryBtn: 'Reject all',
                    showPreferencesBtn: 'Set Cookie preferences',
                    footer: `
                            <a href="https://www.prezly.com/help/privacy-policy-v2">Privacy Policy</a>
                            <a href="https://www.prezly.com/help/terms-of-service--terms-of-use" target="_blank">Terms of Service</a>
                        `,
                },
                preferencesModal: {
                    title: 'Manage cookie preferences',
                    acceptAllBtn: 'Accept all',
                    acceptNecessaryBtn: 'Reject all',
                    savePreferencesBtn: 'Accept current selection',
                    closeIconLabel: 'Close modal',
                    sections: [
                        {
                            title: 'This website uses cookies to enhance your browsing experience and analyze web traffic',
                            description:
                                'Please specify your cookie preferences by using the form below',
                        },
                        {
                            title: 'Strictly Necessary cookies',
                            description:
                                'These cookies are essential for the proper functioning of the website and cannot be disabled. ' +
                                'We use these cookies to store your cookie preferences and preferred language.',

                            //this field will generate a toggle linked to the 'necessary' category
                            linkedCategory: CAT_NECESSARY,
                        },
                        {
                            title: 'Analytics',
                            description:
                                'These cookies are used for analytical purposes. The main analytical tool we use is Mixpanel.com. ' +
                                'The cookies store basic information about your profile, session and browser and the actions you take on the website',
                            linkedCategory: CAT_ANALYTICS,
                        },
                        {
                            title: 'Advertising',
                            description:
                                'Prezly uses Google Advertising for advertising including serving and rendering ads, personalizing ads (depending on your ad settings at' +
                                ' <a href="https://g.co/adsettings">g.co/adsettings</a>), limiting the number of times an ad is shown to a user, muting ads ' +
                                'you have chosen to stop seeing, and measuring the effectiveness of ads.',
                            linkedCategory: CAT_ADVERTISEMENT,
                        },
                        {
                            title: 'More information',
                            description:
                                'For any queries in relation to our <a href="https://www.prezly.com/help/privacy-policy-v2">Privacy Policy</a> and your choices, ' +
                                'Feel free to <a href="/talk-to-us">contact us</a>',
                        },
                    ],
                },
            },
        },
    },
    // Trigger consent update when user choices change
    onFirstConsent: () => {
        initaliseAnalytics();
        updateGtagConsent();
    },
    onConsent: () => {
        initaliseAnalytics();
        updateGtagConsent();
    },
    onChange: () => {
        initaliseAnalytics();
        updateGtagConsent();
    },
};

export function CookieConsent() {
    useEffect(() => {
        CookieConsentLibrary.run(config);
        initaliseAnalytics();

        segment.ready(() => {
            if (!window.gtag) return;

            window.gtag('consent', 'default', {
                [SERVICE_AD_STORAGE]: 'denied',
                [SERVICE_AD_USER_DATA]: 'denied',
                [SERVICE_AD_PERSONALIZATION]: 'denied',
                [SERVICE_ANALYTICS_STORAGE]: 'denied',
            });
        });
    }, []);

    return null;
}
